import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

type FingerSize = "small" | "medium" | "large";

// Map finger sizes to millimeter ranges (using the midpoint)
export const FINGER_SIZE_MM = {
  small: 16,   // 15-17mm
  medium: 19,  // 18-20mm
  large: 22,   // 21-23mm
} as const;

export function useFingerSize(userId: string | null | undefined) {
  const queryClient = useQueryClient();

  // Fetch finger size from database
  const { data: fingerSize, isLoading } = useQuery({
    queryKey: ["finger-size", userId],
    queryFn: async () => {
      if (!userId) return null;
      
      const { data, error } = await supabase
        .from("profiles_internal")
        .select("finger_size_mm")
        .eq("id", userId)
        .maybeSingle();
      
      if (error) {
        console.error("Error fetching finger size:", error);
        return null;
      }
      
      // Convert mm value back to size category
      if (data?.finger_size_mm) {
        const mm = data.finger_size_mm;
        if (mm >= 15 && mm <= 17) return "small";
        if (mm >= 18 && mm <= 20) return "medium";
        if (mm >= 21 && mm <= 23) return "large";
        // Default to medium if stored value doesn't match our ranges
        return "medium";
      }
      
      return null;
    },
    enabled: !!userId,
  });

  // Save finger size to database
  const saveFingerSizeMutation = useMutation({
    mutationFn: async (size: FingerSize) => {
      if (!userId) throw new Error("No user ID");
      
      const mm = FINGER_SIZE_MM[size];
      
      const { error } = await supabase
        .from("profiles_internal")
        .update({ finger_size_mm: mm })
        .eq("id", userId);
      
      if (error) throw error;
      
      return size;
    },
    onSuccess: (size) => {
      // Invalidate the query to refetch the data
      queryClient.invalidateQueries({ queryKey: ["finger-size", userId] });
    },
  });

  const saveFingerSize = async (size: FingerSize) => {
    await saveFingerSizeMutation.mutateAsync(size);
  };

  return {
    fingerSize,
    isLoading,
    saveFingerSize,
    fingerSizeMm: fingerSize ? FINGER_SIZE_MM[fingerSize] : null,
  };
}
